import React, { useContext } from 'react';
import { Button, Theme, makeStyles } from '@material-ui/core';
import { FileWithPreview } from 'react-dropzone';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { DropzoneFilesIcon } from '../Icons';
import { GraySmall, red } from '../../theme/colors';
import MemoBaseTypography from '../Text/BaseTypography';
import { ContractTemplateForm } from './ContractsBuilderPage';
import { FileDropzone } from '../FileDropzone';
import { FileInputUploadItem } from '../UI/FileUploadInputItem';
import { ContractBuilderContext } from '../../context/contractBuilderContext';
import {
  ResetESignToInitialValues,
  loadPagesError,
  requestLoadPages,
} from 'src/store/signaturePage/actions';
import { useCreateContractFilesMutation } from 'src/services/api/contractsApi';
import { S3Utils } from 'src/utils';
import { useProgressUpload } from 'src/hooks/useProgressUpload';

const useStyles = makeStyles((theme: Theme) => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed #ccc',
    padding: theme.spacing(3.25),
    gap: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  errorDropzone: {
    borderColor: red,
  },
  filePreview: {
    boxShadow: 'none',
    maxWidth: 'unset',
  },
  errorText: {
    color: red,
    textAlign: 'right',
  },
}));

interface ContractFileDropzoneProps {
  name: keyof ContractTemplateForm;
}

/**
 * Drag-and-drop input for uploading contract template PDFs.
 * @param {string} name - The name of the formik field associated with this input.
 *
 * @returns {JSX.Element} The JSX element rendering the contract template PDF upload input.
 */
export const ContractFileDropzone = ({ name }: ContractFileDropzoneProps) => {
  const dispatch = useDispatch();
  const { setContractPdfInfo } = useContext(ContractBuilderContext);
  const classes = useStyles();
  const { errors, setFieldValue, touched, setFieldTouched } = useFormikContext<
    ContractTemplateForm
  >();

  const [createContractFile] = useCreateContractFilesMutation();

  const {
    startUploadFiles,
    setUploadProgress,
    exitUploadFiles,
  } = useProgressUpload();

  const [
    selectedPdfFile,
    setSelectedPdfFile,
  ] = React.useState<FileWithPreview | null>(null);

  /**
   * Handles the event when one or more files are dropped into the dropzone.
   *
   * @param {FileWithPreview[]} acceptedFiles - An array of accepted files.
   */
  const handleFileDrop = async (acceptedFiles: FileWithPreview[]) => {
    setFieldTouched(name);
    // If no accepted files, exit early.
    if (!acceptedFiles.length) return;

    const file = acceptedFiles[0];

    setFieldValue(name, file.name);
    setSelectedPdfFile(file);

    const filePaths = await S3Utils.uploadFilesToS3({
      acceptedFiles,
      targetPath: '',
      startUploadFiles,
      setUploadProgress,
      exitUploadFiles,
      ignoreFolders: false,
    });

    const filePath = filePaths[0];
    dispatch(requestLoadPages());
    const response = await createContractFile(filePath.key);
    if ('error' in response) {
      dispatch(loadPagesError('Failed to create the contract file'));
      return;
    }
    setContractPdfInfo({
      fileKey: filePath.key,
      pageFileKeys: response.data.pageKeys,
    });
  };

  /**
   * Clears the selected PDF file and resets the form field value.
   */
  const handleRemoveFile = () => {
    setFieldValue(name, '');
    setSelectedPdfFile(null);
    dispatch(ResetESignToInitialValues());
  };

  const hasError = touched[name] && errors[name];

  if (selectedPdfFile) {
    return (
      <FileInputUploadItem
        className={classes.filePreview}
        name={selectedPdfFile.name}
        size={selectedPdfFile.size}
        extension="pdf"
        readOnly={false}
        onRemove={handleRemoveFile}
      />
    );
  }

  return (
    <div>
      <MemoBaseTypography fontType="13Medium">
        Upload a .pdf file
      </MemoBaseTypography>
      <FileDropzone accept={['application/pdf']} onDrop={handleFileDrop}>
        <div
          className={classNames(classes.dropzone, {
            [classes.errorDropzone]: hasError,
          })}
        >
          <DropzoneFilesIcon
            style={{
              fontSize: 60,
            }}
          />
          <Button variant="contained" color="secondary" size="medium">
            Choose a file
          </Button>

          <MemoBaseTypography
            fontType="12Medium"
            style={{
              color: GraySmall,
            }}
          >
            Or drop a file to upload
          </MemoBaseTypography>
        </div>
      </FileDropzone>
      {hasError && (
        <MemoBaseTypography fontType="13Regular" className={classes.errorText}>
          {errors[name]}
        </MemoBaseTypography>
      )}
    </div>
  );
};
